import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import AdminLayout from "@/components/AdminLayout";
import NetworkLoader from "@/components/NetworkLoader";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Download, UploadCloud, Loader2, CheckCircle2, AlertTriangle, History,
  Database, ShieldCheck, FileArchive, RotateCcw, ChevronDown, X, Sparkles,
} from "lucide-react";

/* ───────────────────────── Costanti visive ───────────────────────── */
const CHUNK_SIZE = 8 * 1024 * 1024; // deve combaciare con CHUNK_MAX backend
const NOISE_BG =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/></svg>";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${u[i]}`;
};

/* ───────────────────────── Sotto-componenti UI (theme-aware) ───────────────────────── */
const GlowBlob = ({ className, lightClassName }) => {
  const { isDark } = useTheme();
  return <div className={`pointer-events-none absolute rounded-full blur-[110px] ${isDark ? className : lightClassName}`} />;
};

const GlassCard = ({ children, className = "", delay = 0 }) => {
  const { isDark } = useTheme();
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`relative rounded-[28px] backdrop-blur-2xl transition-colors duration-300 ${
        isDark ? "border border-white/10 bg-white/[0.04] shadow-[0_8px_50px_rgba(0,0,0,0.45)]"
               : "border border-black/[0.06] bg-white/85 shadow-[0_10px_40px_rgba(0,0,0,0.07)]"
      } ${className}`}
    >
      <div className={`pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent to-transparent ${isDark ? "via-white/25" : "via-black/10"}`} />
      {children}
    </motion.div>
  );
};

const StatChip = ({ label, value, tone }) => {
  const { isDark } = useTheme();
  const tones = isDark ? {
    new: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
    update: "bg-amber-400/10 text-amber-300 border-amber-400/20",
    same: "bg-white/[0.06] text-zinc-400 border-white/10",
  } : {
    new: "bg-emerald-50 text-emerald-700 border-emerald-200",
    update: "bg-amber-50 text-amber-700 border-amber-200",
    same: "bg-zinc-100 text-zinc-500 border-zinc-200",
  };
  return (
    <span data-testid={`stat-chip-${tone}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${tones[tone]}`}>
      <span className="font-mono font-semibold">{value}</span> {label}
    </span>
  );
};

const PrimaryButton = ({ children, onClick, disabled, loading, testid, variant = "solid" }) => {
  const { isDark } = useTheme();
  const base = "relative inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed";
  let styles;
  if (variant === "solid") {
    styles = "text-white bg-gradient-to-br from-[#E08A4F] via-[#C25934] to-[#9B3F23] shadow-[0_6px_24px_rgba(194,89,52,0.35)] hover:shadow-[0_10px_34px_rgba(194,89,52,0.55)] hover:-translate-y-0.5";
  } else {
    styles = isDark
      ? "text-zinc-300 bg-white/[0.05] border border-white/10 hover:bg-white/[0.09] hover:text-white"
      : "text-zinc-600 bg-black/[0.03] border border-black/10 hover:bg-black/[0.06] hover:text-zinc-900";
  }
  return (
    <motion.button whileTap={{ scale: 0.97 }} data-testid={testid} onClick={onClick} disabled={disabled} className={`${base} ${styles}`}>
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </motion.button>
  );
};

const DiffRow = ({ coll, d }) => {
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const hasChanges = d.new > 0 || d.update > 0;
  const samples = [...(d.new_sample || []).map((s) => ["new", s]), ...(d.update_sample || []).map((s) => ["update", s])];
  return (
    <div data-testid={`diff-row-${coll}`} className={`rounded-2xl border px-4 py-3 ${isDark ? "border-white/[0.08] bg-black/20" : "border-black/[0.06] bg-black/[0.025]"}`}>
      <button onClick={() => samples.length && setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 text-left">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`text-sm font-medium truncate ${isDark ? "text-zinc-100" : "text-zinc-800"}`}>{d.label}</span>
          <span className="text-[11px] text-zinc-500 font-mono">{d.total} tot.</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <StatChip label="nuovi" value={d.new} tone="new" />
          <StatChip label="agg." value={d.update} tone="update" />
          <StatChip label="invariati" value={d.unchanged} tone="same" />
          {samples.length > 0 && <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />}
        </div>
      </button>
      <AnimatePresence>
        {open && samples.length > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className={`mt-3 pt-3 border-t flex flex-wrap gap-1.5 ${isDark ? "border-white/5" : "border-black/5"}`}>
              {samples.slice(0, 30).map(([kind, s], i) => (
                <span key={i} className={`font-mono text-[10px] px-2 py-1 rounded-md ${
                  kind === "new"
                    ? isDark ? "bg-emerald-400/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                    : isDark ? "bg-amber-400/10 text-amber-300" : "bg-amber-50 text-amber-700"
                }`}>{s}</span>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!hasChanges && d.total === 0 && <div className="mt-1 text-[11px] text-zinc-500">Nessun dato in questa categoria.</div>}
    </div>
  );
};

/* ───────────────────────── Pagina ───────────────────────── */
export default function CasaMadreCatalogTransfer() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const isSuperAdmin = user?.role === "superadmin";

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");

  const [exporting, setExporting] = useState(false);

  const [importState, setImportState] = useState("idle"); // idle | uploading | preview_loading | preview | applying | done
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [job, setJob] = useState(null);
  const [includeSettings, setIncludeSettings] = useState(true);
  const [result, setResult] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get("/tenants", { params: { type: "casa_madre" } })
      .then(({ data }) => setTenants(data || []))
      .catch(() => toast.error("Impossibile caricare l'elenco Case Madre"));
  }, [isSuperAdmin]);

  const canAct = !isSuperAdmin || !!tenantId;

  const loadJobs = useCallback(() => {
    if (isSuperAdmin && !tenantId) { setJobs([]); setJobsLoading(false); return; }
    setJobsLoading(true);
    api.get("/catalog-transfer/jobs", { params: tenantId ? { tenant_id: tenantId } : {} })
      .then(({ data }) => setJobs(data || []))
      .catch(() => {})
      .finally(() => setJobsLoading(false));
  }, [isSuperAdmin, tenantId]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const resetImport = () => {
    setImportState("idle"); setProgress(0); setJob(null); setResult(null); setIncludeSettings(true);
  };

  const cancelImport = async () => {
    const current = job;
    resetImport();
    if (current?.id) {
      try { await api.delete(`/catalog-transfer/jobs/${current.id}`); } catch { /* best-effort cleanup */ }
    }
  };

  const handleExport = async () => {
    if (!canAct) { toast.error("Seleziona prima una Casa Madre"); return; }
    setExporting(true);
    try {
      const res = await api.get("/catalog-transfer/export", {
        params: tenantId ? { tenant_id: tenantId } : {},
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "catalogo.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Catalogo esportato (${fmtBytes(blob.size)})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  const startImport = async (file) => {
    if (!canAct) { toast.error("Seleziona prima una Casa Madre"); return; }
    if (!file.name.toLowerCase().endsWith(".zip")) { toast.error("Seleziona un file .zip esportato da TotalKlick Hub"); return; }
    setImportState("uploading");
    setProgress(0);
    const uploadId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    try {
      for (let i = 0; i < total; i++) {
        const chunkBlob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", String(i));
        fd.append("total", String(total));
        fd.append("chunk", chunkBlob, file.name);
        await api.post("/catalog-transfer/upload-chunk", fd);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      setImportState("preview_loading");
      const { data } = await api.post("/catalog-transfer/preview", {
        upload_id: uploadId, tenant_id: tenantId || undefined,
      });
      setJob(data);
      setImportState("preview");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il caricamento del file");
      resetImport();
    }
  };

  const applyImport = async () => {
    setImportState("applying");
    try {
      const { data } = await api.post(`/catalog-transfer/apply/${job.id}`, { include_settings: includeSettings });
      setResult(data);
      setImportState("done");
      toast.success("Catalogo importato con successo");
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'importazione");
      setImportState("preview");
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) startImport(file);
  };
  const onFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) startImport(file);
    e.target.value = "";
  };

  const totalCreated = useMemo(() => result ? Object.values(result.collections || {}).reduce((s, c) => s + (c.created || 0), 0) : 0, [result]);
  const totalUpdated = useMemo(() => result ? Object.values(result.collections || {}).reduce((s, c) => s + (c.updated || 0), 0) : 0, [result]);

  return (
    <AdminLayout>
      <div data-testid="catalog-transfer-page" className={`relative min-h-screen overflow-hidden transition-colors duration-300 ${isDark ? "bg-[#08080a] text-zinc-100" : "bg-[#F8F6F2] text-zinc-900"}`}>
        {/* Sfondo atmosferico */}
        <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: `url("${NOISE_BG}")`, mixBlendMode: isDark ? "overlay" : "multiply", opacity: isDark ? 0.4 : 0.05 }} />
        <GlowBlob className="w-[520px] h-[520px] bg-[#C25934] -top-40 -right-20 opacity-40" lightClassName="w-[520px] h-[520px] bg-[#C25934] -top-40 -right-20 opacity-[0.07]" />
        <GlowBlob className="w-[420px] h-[420px] bg-[#1fb6c9] bottom-0 -left-24 opacity-[0.12]" lightClassName="w-[420px] h-[420px] bg-[#1fb6c9] bottom-0 -left-24 opacity-[0.05]" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 py-14">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className={`overline mb-3 flex items-center gap-2 ${isDark ? "text-[#E8935B]" : "text-[#A84526]"}`}>
                <Sparkles className="w-3.5 h-3.5" /> {isSuperAdmin ? "SuperAdmin" : "Casa Madre"} · Strumenti avanzati
              </div>
              <h1 className={`text-4xl sm:text-5xl font-heading font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-br ${
                isDark ? "from-white via-zinc-100 to-zinc-400" : "from-zinc-900 via-zinc-800 to-zinc-500"
              }`}>
                Esporta &amp; Importa Catalogo
              </h1>
              <p className={`mt-3 text-base max-w-xl ${isDark ? "text-zinc-400" : "text-zinc-600"}`}>
                Migra l'intero catalogo — prodotti, varianti, stock, librerie e impostazioni brand — tra ambienti diversi con un unico pacchetto ZIP.
              </p>
            </div>

            {isSuperAdmin && (
              <div className="shrink-0">
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Casa Madre</label>
                <Select value={tenantId} onValueChange={(v) => { setTenantId(v); resetImport(); }}>
                  <SelectTrigger data-testid="catalog-transfer-tenant-select" className={`w-64 rounded-xl px-4 py-2.5 h-auto text-sm focus:border-[#C25934]/60 focus:ring-2 focus:ring-[#C25934]/20 ${
                    isDark ? "bg-white/[0.06] border-white/10 text-zinc-100 [&>span]:text-zinc-100" : "bg-black/[0.03] border-black/10 text-zinc-900 [&>span]:text-zinc-900"
                  }`}>
                    <SelectValue placeholder="— Seleziona —" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? "bg-[#111113] border-white/10 text-zinc-100" : ""}>
                    {tenants.map((t) => (
                      <SelectItem key={t.id} value={t.id} className={isDark ? "focus:bg-white/10 focus:text-white" : ""}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </motion.div>

          {isSuperAdmin && !tenantId && (
            <div className={`mb-8 px-5 py-3 rounded-2xl border text-sm flex items-center gap-2 ${
              isDark ? "border-amber-400/20 bg-amber-400/[0.06] text-amber-300" : "border-amber-200 bg-amber-50 text-amber-800"
            }`}>
              <AlertTriangle className="w-4 h-4 shrink-0" /> Seleziona una Casa Madre per abilitare export/import.
            </div>
          )}

          {/* Grid principale */}
          <div className="grid lg:grid-cols-2 gap-7">
            {/* ───── Esporta ───── */}
            <GlassCard className="p-8" delay={0.05}>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#E08A4F] to-[#9B3F23] flex items-center justify-center shadow-lg shadow-orange-900/30 mb-5">
                <Download className="w-[22px] h-[22px] text-white" />
              </div>
              <h2 className={`text-xl font-heading font-semibold mb-2 ${isDark ? "text-white" : "text-zinc-900"}`}>Esporta catalogo</h2>
              <p className={`text-sm mb-6 leading-relaxed ${isDark ? "text-zinc-400" : "text-zinc-600"}`}>
                Genera un pacchetto <span className={`font-medium ${isDark ? "text-zinc-200" : "text-zinc-800"}`}>.zip</span> completo con JSON dei dati e tutte le immagini collegate, pronto per essere importato in un altro ambiente.
              </p>
              <ul className={`space-y-2 mb-8 text-[13px] ${isDark ? "text-zinc-400" : "text-zinc-600"}`}>
                {["Prodotti madre e varianti", "Stock e SKU realizzati", "Librerie, famiglie e componenti", "Impostazioni brand (colori, font, template)"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> {t}</li>
                ))}
              </ul>
              <PrimaryButton testid="catalog-transfer-export-btn" onClick={handleExport} disabled={!canAct || exporting} loading={exporting}>
                <FileArchive className="w-4 h-4" /> {exporting ? "Preparazione pacchetto…" : "Scarica pacchetto ZIP"}
              </PrimaryButton>
            </GlassCard>

            {/* ───── Importa ───── */}
            <GlassCard className="p-8" delay={0.12}>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-zinc-500 to-zinc-700 flex items-center justify-center shadow-lg mb-5">
                <UploadCloud className="w-[22px] h-[22px] text-white" />
              </div>
              <h2 className={`text-xl font-heading font-semibold mb-2 ${isDark ? "text-white" : "text-zinc-900"}`}>Importa catalogo</h2>
              <p className={`text-sm mb-6 leading-relaxed ${isDark ? "text-zinc-400" : "text-zinc-600"}`}>
                Carica un pacchetto esportato: i dati vengono confrontati per SKU/codice — creati o aggiornati, <span className={`font-medium ${isDark ? "text-zinc-200" : "text-zinc-800"}`}>mai cancellati</span>.
              </p>

              <AnimatePresence mode="wait">
                {importState === "idle" && (
                  <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <label
                      data-testid="catalog-transfer-dropzone"
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={onDrop}
                      className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors ${
                        !canAct ? `opacity-40 cursor-not-allowed ${isDark ? "border-white/10" : "border-black/10"}` :
                        dragOver ? "border-[#C25934]/70 bg-[#C25934]/[0.06]" :
                        isDark ? "border-white/15 hover:border-white/25 hover:bg-white/[0.02]" : "border-black/15 hover:border-black/25 hover:bg-black/[0.02]"
                      }`}
                    >
                      <UploadCloud className="w-7 h-7 text-zinc-500" />
                      <div className={`text-sm ${isDark ? "text-zinc-300" : "text-zinc-700"}`}>Trascina qui il file <span className={`font-mono ${isDark ? "text-zinc-100" : "text-zinc-900"}`}>.zip</span></div>
                      <div className="text-xs text-zinc-500">oppure clicca per selezionarlo (max 500MB)</div>
                      <input data-testid="catalog-transfer-file-input" type="file" accept=".zip" className="hidden" disabled={!canAct} onChange={onFileInput} />
                    </label>
                  </motion.div>
                )}

                {importState === "uploading" && (
                  <motion.div key="uploading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-6">
                    <div className={`flex items-center justify-between text-xs mb-2 ${isDark ? "text-zinc-400" : "text-zinc-500"}`}>
                      <span>Caricamento in corso…</span>
                      <span className="font-mono">{progress}%</span>
                    </div>
                    <div className={`h-2 rounded-full overflow-hidden ${isDark ? "bg-white/[0.06]" : "bg-black/[0.06]"}`}>
                      <motion.div data-testid="catalog-transfer-progress-bar" className="h-full rounded-full bg-gradient-to-r from-[#E08A4F] to-[#C25934]"
                        animate={{ width: `${progress}%` }} transition={{ ease: "easeOut" }} />
                    </div>
                  </motion.div>
                )}

                {importState === "preview_loading" && (
                  <motion.div key="preview_loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-6 flex justify-center">
                    <NetworkLoader size={88} label="Calcolo differenze con il catalogo attuale…" />
                  </motion.div>
                )}

                {importState === "applying" && (
                  <motion.div key="applying" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-6 flex justify-center">
                    <NetworkLoader size={88} label="Applicazione del catalogo in corso…" />
                  </motion.div>
                )}

                {importState === "preview" && job && (
                  <motion.div key="preview" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-3">
                    <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                      <span>Da <span className={`font-medium ${isDark ? "text-zinc-300" : "text-zinc-700"}`}>{job.source?.name || "sorgente sconosciuta"}</span> · esportato {fmtDate(job.exported_at)}</span>
                      <button data-testid="catalog-transfer-cancel-x" onClick={cancelImport} className={`text-zinc-500 ${isDark ? "hover:text-white" : "hover:text-zinc-900"}`}><X className="w-4 h-4" /></button>
                    </div>
                    <div className="max-h-[340px] overflow-y-auto space-y-2 pr-1">
                      {Object.entries(job.diff || {}).map(([coll, d]) => <DiffRow key={coll} coll={coll} d={d} />)}
                    </div>
                    <div className="flex items-center gap-2 text-[12px] text-zinc-500 pt-1">
                      <Database className="w-3.5 h-3.5" /> {job.media?.total || 0} immagini nel pacchetto · {job.media?.already_present || 0} già presenti
                    </div>
                    {job.tenant_settings && (
                      <label className={`flex items-center gap-2 text-sm pt-2 cursor-pointer select-none ${isDark ? "text-zinc-300" : "text-zinc-700"}`}>
                        <input data-testid="catalog-transfer-include-settings-checkbox" type="checkbox" checked={includeSettings}
                          onChange={(e) => setIncludeSettings(e.target.checked)}
                          className="w-4 h-4 rounded accent-[#C25934]" />
                        Includi impostazioni brand (colori, font, template)
                      </label>
                    )}
                    <div className="flex gap-3 pt-3">
                      <PrimaryButton testid="catalog-transfer-cancel-btn" variant="ghost" onClick={cancelImport}>Annulla</PrimaryButton>
                      <PrimaryButton testid="catalog-transfer-confirm-btn" onClick={applyImport}>
                        <ShieldCheck className="w-4 h-4" /> Conferma e importa
                      </PrimaryButton>
                    </div>
                  </motion.div>
                )}

                {importState === "done" && result && (
                  <motion.div key="done" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="space-y-4" data-testid="catalog-transfer-result">
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${isDark ? "bg-emerald-400/[0.08] border-emerald-400/20" : "bg-emerald-50 border-emerald-200"}`}>
                      <CheckCircle2 className={`w-5 h-5 shrink-0 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                      <div className={`text-sm ${isDark ? "text-emerald-200" : "text-emerald-800"}`}>
                        Import completato — <span className="font-semibold">{totalCreated} creati</span>, <span className="font-semibold">{totalUpdated} aggiornati</span>, {result.media?.uploaded || 0} immagini caricate.
                      </div>
                    </div>
                    <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
                      {Object.entries(result.collections || {}).map(([coll, c]) => (
                        <div key={coll} className={`flex items-center justify-between text-[13px] px-3 py-2 rounded-xl ${isDark ? "bg-white/[0.03]" : "bg-black/[0.03]"}`}>
                          <span className={isDark ? "text-zinc-300" : "text-zinc-700"}>{c.label}</span>
                          <div className="flex gap-1.5"><StatChip label="creati" value={c.created} tone="new" /><StatChip label="agg." value={c.updated} tone="update" /></div>
                        </div>
                      ))}
                    </div>
                    <PrimaryButton testid="catalog-transfer-reset-btn" variant="ghost" onClick={resetImport}>
                      <RotateCcw className="w-4 h-4" /> Nuova importazione
                    </PrimaryButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </GlassCard>
          </div>

          {/* Storico */}
          <GlassCard className="p-8 mt-7" delay={0.2}>
            <div className="flex items-center gap-2 mb-5">
              <History className="w-[18px] h-[18px] text-zinc-500" />
              <h3 className={`text-base font-heading font-semibold ${isDark ? "text-white" : "text-zinc-900"}`}>Storico importazioni</h3>
            </div>
            {jobsLoading ? (
              <div className="flex items-center gap-2 text-sm text-zinc-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
            ) : jobs.length === 0 ? (
              <div className="text-sm text-zinc-500 py-6 text-center">Nessuna importazione registrata finora.</div>
            ) : (
              <div data-testid="catalog-transfer-jobs-list" className="space-y-2">
                {jobs.map((j) => (
                  <div key={j.id} data-testid={`catalog-transfer-job-row-${j.id}`} className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-2xl border ${
                    isDark ? "bg-white/[0.03] border-white/5" : "bg-black/[0.02] border-black/[0.05]"
                  }`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${j.status === "applied" ? "bg-emerald-400" : j.status === "failed" ? "bg-red-400" : "bg-amber-400"}`} />
                      <div className="min-w-0">
                        <div className={`text-sm truncate ${isDark ? "text-zinc-200" : "text-zinc-800"}`}>{j.source?.name || "—"}</div>
                        <div className="text-[11px] text-zinc-500">{fmtDate(j.created_at)}</div>
                      </div>
                    </div>
                    <div className="text-[11px] text-zinc-500">{j.status === "applied" ? "Applicato" : j.status === "failed" ? "Fallito" : j.status}</div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        </div>
      </div>
    </AdminLayout>
  );
}
